<template>
  <ModalDialog
    ref="dialog"
    title="Panel Allocation"
    icon="fa-layer-group"
    :showFooter="true"
    closeTitle="Cancel"
    submitTitle="Update"
    @closed="onClosed"
    @submitted="onSubmitted">

    <FormGroup v-bind:form="form" :labelColumnSize="3">

      <FormRow label="Panel Size" v-if="sheetAllocation.productionPanel">
        <FormStaticLabel>{{sheetAllocation.productionPanel.panelHeight}}mm x {{sheetAllocation.productionPanel.panelWidth}}mm</FormStaticLabel>
      </FormRow>
      <FormRow label="Angle">
        <FormStaticLabel>{{sheetAllocation.angle}}&deg;</FormStaticLabel>
      </FormRow>
      <FormRow label="Total Size" helpTip="Includes trim">
        <FormStaticLabel>{{sheetAllocation.sourceHeight}}mm x {{sheetAllocation.sourceWidth}}mm</FormStaticLabel>
      </FormRow>

      <FormHorizontalRule />

      <FormRow label="Use Custom Size" helpTip="If set, overrides the rectangle size for the panel. Setting this will disable shaped cuts">
        <FormSwitch booleanMode v-model="sheetAllocation.isPanelSizeOverridden" />
      </FormRow>
      <FormRow label="Custom Allocation Size" v-show="sheetAllocation.isPanelSizeOverridden" :fieldCount="2">
        <template v-slot:field1>
          <FormInputText type="number" v-model="sheetAllocation.sourceHeight" prepend="Height" addon="mm" />
        </template>
        <template v-slot:field2>
          <FormInputText type="number" v-model="sheetAllocation.sourceWidth" prepend="Width" addon="mm" />
        </template>
      </FormRow>

      <FormRow label="Enable shaped cuts" helpTip="By default, all shaped cuts for editor based panels will be made. Turning this off disables shaped cuts and only the outside rectangle will be cut">
        <FormSwitch booleanMode v-show="isEnableShapeCutsAvailable" :value="!sheetAllocation.isPanelShapeCuttingDisabled" @update="sheetAllocation.isPanelShapeCuttingDisabled = !$event" />
        <FormStaticLabel v-show="!isEnableShapeCutsAvailable">Disabled</FormStaticLabel>
      </FormRow>

      <FormRow label="Custom Trim" helpTip="If set, overrides the trim around the panel shape. Enabling this will override trim calculated from edgework and cuts">
        <FormSwitch booleanMode v-model="sheetAllocation.isPanelTrimOverridden" v-show="isTrimOverrideAvailable" />
        <FormStaticLabel v-show="!isTrimOverrideAvailable">Disabled</FormStaticLabel>
      </FormRow>
      <FormRow label="Custom Panel Trim" v-show="sheetAllocation.isPanelTrimOverridden && isTrimOverrideAvailable" :fieldCount="2">
        <template v-slot:field1>
          <FormInputText type="number" v-model="sheetAllocation.trimTop" prepend="Top" addon="mm" />
        </template>
        <template v-slot:field2>
          <FormInputText type="number" v-model="sheetAllocation.trimRight" prepend="Right" addon="mm" />
        </template>
      </FormRow>
      <FormRow v-show="sheetAllocation.isPanelTrimOverridden && isTrimOverrideAvailable" :fieldCount="2">
        <template v-slot:field1>
          <FormInputText type="number" v-model="sheetAllocation.trimBottom" prepend="Bottom" addon="mm" />
        </template>
        <template v-slot:field2>
          <FormInputText type="number" v-model="sheetAllocation.trimLeft" prepend="Left" addon="mm" />
        </template>
      </FormRow>

      <div v-show="isTemplateHandlingRequired">
        <FormHorizontalRule />
        <FormRow label="Template Handled" helpTip="If set, flags the panel as having its template handled and can continue cutting">
          <FormSwitch booleanMode v-model="sheetAllocation.isPanelTemplateHandled" />
        </FormRow>
      </div>

      <div v-show="isPanelCutoutsRequireHandling">
        <FormHorizontalRule />
        <FormRow label="Cutouts Handled" helpTip="If set, flags the panel as having any of its cutouts handled at optimisation and can continue cutting">
          <FormSwitch booleanMode v-model="sheetAllocation.isPanelCutoutsHandled" />
        </FormRow>
      </div>

    </FormGroup>
  </ModalDialog>
</template>

<style scoped>

</style>

<script>
import _ from 'lodash'

import OptimiserUtils from '@/apps/glass-production-station/src/classes/optimiserUtils'

import form from '@/mixins/form'
import ModalDialog from '@/components/modalDialog.vue'
import modalDialog from '@/mixins/modalDialog'

export default {
  name: 'OptimiserPanelEditDialog',
  mixins: [
    modalDialog,
    form
  ],
  components: {
    ModalDialog
  },
  props: {
  },
  data() {
    return {
      form: {},
      sheetAllocation: {},
      trimData: {
        trimTop: 0,
        trimRight: 0,
        trimBottom: 0,
        trimLeft: 0
      }
    }
  },
  watch: {
  },
  created() {
  },
  mounted() {
  },
  computed: {
    isTrimOverrideAvailable() {
      // Trim override is disabled if panel size is overriden or shape cutting is disabled
      return !(this.sheetAllocation.isPanelSizeOverridden || this.sheetAllocation.isPanelShapeCuttingDisabled)
    },
    isEnableShapeCutsAvailable() {
      // Enable shaped cuts is disabled if panel size is overriden
      return !this.sheetAllocation.isPanelSizeOverridden
    },
    isTemplateHandlingRequired() {
      if(this.sheetAllocation.orderItem)
        return this.sheetAllocation.orderItem.isTemplateHandlingRequired
      return false
    },
    isPanelCutoutsRequireHandling() {
      if(this.sheetAllocation.isPanelSizeOverridden || this.sheetAllocation.isPanelShapeCuttingDisabled)
        return false
      return this.sheetAllocation.isPanelCutoutsRequireHandling
    }
  },
  methods: {
    show(params) {
      this.form = {}
      this.submitting = false

      this.sheetAllocation = _.cloneDeep(params)

      // Rotate trims to match angle & display
      let transformedTrim = OptimiserUtils.calculateTransformedTrimValues(
        this.sheetAllocation.trimTop,
        this.sheetAllocation.trimRight,
        this.sheetAllocation.trimBottom,
        this.sheetAllocation.trimLeft,
        this.sheetAllocation.angle,
        this.sheetAllocation.flippedHorz,
        this.sheetAllocation.flippedVert
      )

      this.sheetAllocation.trimTop = transformedTrim.trimTop
      this.sheetAllocation.trimRight = transformedTrim.trimRight
      this.sheetAllocation.trimBottom = transformedTrim.trimBottom
      this.sheetAllocation.trimLeft = transformedTrim.trimLeft

      return modalDialog.methods.show.call(this, arguments)
    },
    onClosed() {
      this.modalClosed(null)
    },
    onSubmitted() {
      this.form = this.clearFormResponse()

      let updateData = {
        isPanelSizeOverridden: this.sheetAllocation.isPanelSizeOverridden,
        isPanelShapeCuttingDisabled: this.sheetAllocation.isPanelShapeCuttingDisabled,
        isPanelTrimOverridden: this.sheetAllocation.isPanelTrimOverridden,
        isPanelTemplateHandled: this.sheetAllocation.isPanelTemplateHandled,
        isPanelCutoutsHandled: this.sheetAllocation.isPanelCutoutsHandled,
        sourceWidth: parseInt(this.sheetAllocation.sourceWidth),
        sourceHeight: parseInt(this.sheetAllocation.sourceHeight)
      }

      if(this.sheetAllocation.isPanelTrimOverridden) {
        updateData.trimTop = parseInt(this.sheetAllocation.trimTop)
        updateData.trimRight = parseInt(this.sheetAllocation.trimRight)
        updateData.trimBottom = parseInt(this.sheetAllocation.trimBottom)
        updateData.trimLeft = parseInt(this.sheetAllocation.trimLeft)

        // Untransform trims back to original coordinates
        let originalTrim = OptimiserUtils.calculateUntransformedTrimValues(
          updateData.trimTop,
          updateData.trimRight,
          updateData.trimBottom,
          updateData.trimLeft,
          this.sheetAllocation.angle,
          this.sheetAllocation.flippedHorz,
          this.sheetAllocation.flippedVert
        )
        updateData.trimTop = originalTrim.trimTop
        updateData.trimRight = originalTrim.trimRight
        updateData.trimBottom = originalTrim.trimBottom
        updateData.trimLeft = originalTrim.trimLeft
      }

      // Clear trim if size overridden / shape cutting disabled
      if(this.sheetAllocation.isPanelSizeOverridden || this.sheetAllocation.isPanelShapeCuttingDisabled) {
        updateData.trimTop = 0
        updateData.trimRight = 0
        updateData.trimBottom = 0
        updateData.trimLeft = 0
      }

      this.modalClosed(updateData)
      this.hide()
    },
  }
}
</script>